import { Row, Workbook, Worksheet } from 'exceljs';
import { AnalysisQuestionReport, DataRequestItem, ProjectQuestionReport } from 'Types/dealGPT';

import { DealGPTDiscoveryView } from '../../../Types/enums';

/**
 * Supported data types for default Excel report creation.
 * NOTE: If you create a new report type using createExcelReport, add the type here.
 */
type ExcelReportData = AnalysisQuestionReport | ProjectQuestionReport | DataRequestItem;

// Default styling constants
const DEFAULT_HEADER_STYLE = {
  height: 35,
  fill: {
    type: 'pattern' as const,
    pattern: 'solid' as const,
    fgColor: { argb: '002060' },
  },
  font: { color: { argb: 'FFFFFF' }, bold: true, size: 14 },
  alignment: { horizontal: 'center' as const },
};

const DEFAULT_DATA_ROW_ALIGNMENT = {
  wrapText: true,
  vertical: 'top' as const,
};

// Styling utility functions
const applyHeaderRowStyle = (row: Row): void => {
  row.height = DEFAULT_HEADER_STYLE.height;
  row.eachCell((cell) => {
    cell.fill = DEFAULT_HEADER_STYLE.fill;
    cell.alignment = DEFAULT_HEADER_STYLE.alignment;
    cell.font = DEFAULT_HEADER_STYLE.font;
  });
};

const applyDataRowStyle = (row: Row): void => {
  row.eachCell((cell) => {
    cell.alignment = DEFAULT_DATA_ROW_ALIGNMENT;
  });
};

const applyDefaultWorksheetStyling = (worksheet: Worksheet): void => {
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) {
      applyHeaderRowStyle(row);
    } else {
      applyDataRowStyle(row);
    }
  });
};

// Generic Excel report factory function
const createExcelReport = <T extends ExcelReportData>(config: {
  worksheetName: string;
  columns: Array<{ header: string; key: string; width: number }>;
  data: T[];
}): Workbook => {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(config.worksheetName);

  worksheet.columns = config.columns;
  worksheet.addRows(config.data);

  applyDefaultWorksheetStyling(worksheet);

  return workbook;
};

// Report creation functions
export const createAnalysisExcelReport = (data: AnalysisQuestionReport[], scope: string) => {
  return createExcelReport<AnalysisQuestionReport>({
    worksheetName: scope,
    columns: [
      { header: 'Section', key: 'pageName', width: 20 },
      { header: 'Question/Area', key: 'prettyQuestion', width: 50 },
      { header: 'Response', key: 'response', width: 70 },
      { header: 'Value', key: 'value', width: 50 },
      { header: 'Documents', key: 'documents', width: 100 },
    ],
    data,
  });
};

export const createDiscoveryExcelReport = (data: ProjectQuestionReport[], reportType: string) => {
  const baseColumns = [
    { header: 'Scope', key: 'scope', width: 20 },
    { header: 'Category', key: 'category', width: 50 },
    { header: 'Status', key: 'status', width: 30 },
    { header: 'Question', key: 'question', width: 50 },
  ];

  const columns =
    reportType === DealGPTDiscoveryView.ALL_QUESTIONS
      ? [
          ...baseColumns,
          { header: 'Response', key: 'response', width: 50 },
          { header: 'Explanation', key: 'explanation', width: 50 },
          { header: 'Documents', key: 'documents', width: 100 },
        ]
      : baseColumns;

  return createExcelReport<ProjectQuestionReport>({
    worksheetName: 'Discovery Report',
    columns,
    data,
  });
};

export const createDataRequestListExcelReport = (data: DataRequestItem[]) => {
  return createExcelReport<DataRequestItem>({
    worksheetName: 'Data Request List',
    columns: [
      { header: 'No.', key: 'key', width: 20 },
      { header: 'Scope', key: 'scope', width: 50 },
      { header: 'Details', key: 'details', width: 100 },
      { header: 'Priority', key: 'priority', width: 50 },
      { header: 'Status', key: 'status', width: 50 },
      { header: 'Notes', key: 'notes', width: 100 },
    ],
    data,
  });
};
